import { useSubscription } from "@/context/subscription-provider";
import { useTransactionModal } from "@/context/transaction-modal-provider";
import delayPromise from "@/utils/delay-promise";
import {
  AsyncJobErrorType,
  classifyAsyncJobError,
  getErrorMessage,
} from "@/utils/error-handling";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useAuth } from "../context/auth-provider";
import { TransactionAPI } from "../services/transaction.service";
import type { PredictionDetailedResult } from "../types/job";
import {
  Category,
  Transaction,
  TransactionType,
} from "../types/transaction.type";
import useCategories from "./use-categories";
import useTransactionTypes from "./use-transaction-types";
import { buildTransactionFilterParams } from "./use-uncategorized-transactions";
import { pollJobStatus } from "../utils/poll-job-status";
import { UseFiltersParams } from "./use-filters";
import { UsePaginationParams } from "./use-pagination";
import { toast } from "./use-toast";

dayjs.extend(utc);

interface UseTransactionsParams {
  search?: string;
  filters?: UseFiltersParams;
  pagination?: UsePaginationParams;
  categories?: Category[];
  types?: TransactionType[];
  setCount?: React.Dispatch<React.SetStateAction<number>>;
  fetchEnabled?: boolean;
}

const buildAsyncJobToast = (error: unknown, context: string) => {
  const classification = classifyAsyncJobError(error);
  const description = getErrorMessage(error, context);

  const titleMap: Record<AsyncJobErrorType, string> = {
    [AsyncJobErrorType.ENQUEUE_VALIDATION]: "Invalid request",
    [AsyncJobErrorType.ENQUEUE_SERVER]: "Server error",
    [AsyncJobErrorType.JOB_FAILURE]: "Job failed",
    [AsyncJobErrorType.POLLING_TIMEOUT]: "Job still running",
    [AsyncJobErrorType.POLLING_CANCELLED]: "Operation cancelled",
    [AsyncJobErrorType.NETWORK_ERROR]: "Network error",
    [AsyncJobErrorType.UNKNOWN]: "Unexpected error",
  };

  return {
    variant: classification.shouldRetry ? "default" : "destructive",
    title: titleMap[classification.type],
    description,
  };
};

const formatDate = (value?: string | Date) => {
  if (!value) return undefined;
  return dayjs.utc(value).format("YYYY-MM-DD");
};

const normalizeRelationId = (
  value?: Category | TransactionType | number | string,
): number | string | undefined => {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && "id" in value) {
    return (value as Category | TransactionType).id;
  }
  return undefined;
};

const useTransactions = (params: UseTransactionsParams = {}) => {
  const {
    search = "",
    filters = {
      customCategories: true,
      categories: [],
      type: "",
      startDate: "",
      endDate: "",
    },
    pagination = {
      page: 1,
      pageSize: 50,
      limit: 50,
      offset: 0,
      order: "descend",
      orderBy: "occurred",
      ordering: "occurred",
      count: 0,
    },
    categories: categoriesOverride,
    types: typesOverride,
    fetchEnabled = true,
    setCount = () => {},
  } = params || {};
  const queryClient = useQueryClient();
  const { hide } = useTransactionModal();

  const { user } = useAuth();
  const { subscriptionStatus } = useSubscription();
  const isAuthenticated = !!user;

  const shouldFetchCategories = fetchEnabled && !categoriesOverride;
  const shouldFetchTypes = fetchEnabled && !typesOverride;

  const { categories: fetchedCategories } = useCategories({
    filters: { ...filters },
    search,
    enabled: shouldFetchCategories,
  });

  const { transactionTypes: fetchedTypes } = useTransactionTypes({
    enabled: shouldFetchTypes,
  });

  const categories = categoriesOverride ?? fetchedCategories ?? [];
  const types = typesOverride ?? fetchedTypes ?? [];

  const controllerRef = React.useRef<AbortController | null>(null);

  /**
   * Predict categories for multiple transactions (ASYNC JOB)
   *
   * This function:
   * 1. Enqueues a prediction job
   * 2. Polls until completion
   * 3. Returns the predicted transactions
   */
  const predictTransactions = async (records: Partial<Transaction>[]) => {
    // Create abort controller for both API call and polling
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;

    // Step 1: Enqueue the job
    const { data: jobResponse } = await TransactionAPI.predict_all(
      records.map((r) => ({
        occurred: formatDate(r.occurred),
        amount: r.amount,
        description: r.description,
      })),
      { signal },
    );

    // Step 2: Poll for completion
    const { data, warning } = await pollJobStatus<PredictionDetailedResult>(
      jobResponse.job_id,
      {
        signal,
        interval: 2000,
        maxDuration: 600000, // 10 minutes
      },
    );

    // Show warning if detailed result expired
    if (warning) {
      toast({
        variant: "default",
        title: "Results partially available",
        description: warning.message,
      });
    }

    // Return transactions from detailed result
    return data?.transactions || data?.predictions || [];
  };

  const predictTransactionsAbort = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
      toast({
        variant: "destructive",
        title: "Prediction cancelled",
        description: "The prediction process has been cancelled.",
      });
    }
  };

  const predictTransactionsMutation = useMutation({
    mutationFn: predictTransactions,
    onSuccess: (data) => {
      // toast({
      //   variant: "default",
      //   title: "Predicted transactions",
      //   description: `${data.length} transactions predicted successfully.`,
      // });
    },
    onError: (error: unknown) => {
      const toastPayload = buildAsyncJobToast(error, "predicting transactions");
      toast(toastPayload);
    },
  });

  const createTransaction = async (record: Partial<Transaction>) => {
    const response = await TransactionAPI.post({
      occurred: formatDate(record.occurred),
      category: normalizeRelationId(record.category),
      type: normalizeRelationId(record.type),
      amount: record.amount,
      description: record.description,
      user: user?.id,
    });
    return response.data;
  };

  const createTransactionMutation = useMutation({
    mutationFn: createTransaction,
    onSuccess: (data, variables, context) => {
      toast({
        variant: "default",
        title: "Transaction created!",
        description: `Transaction ${variables.description} created successfully.`,
      });
      queryClient.invalidateQueries("transactions");
      queryClient.invalidateQueries("categories");
      hide();
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Failed to create transaction",
        description: "Please try again later.",
      });
    },
  });

  const createTransactions = async (records: Partial<Transaction>[]) => {
    controllerRef.current = new AbortController();
    const { signal } = controllerRef.current;
    const response = await TransactionAPI.bulkCreate(
      records.map((r) => ({
        occurred: formatDate(r.occurred),
        category: normalizeRelationId(r.category),
        type: normalizeRelationId(r.type),
        amount: r.amount,
        description: r.description,
        user: user?.id,
      })),
      { signal },
    );
    return response.data;
  };

  const createTransactionsAbort = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
      toast({
        variant: "destructive",
        title: "Transaction creation cancelled",
        description: "The transaction creation process has been cancelled.",
      });
    }
  };

  const createTransactionsMutation = useMutation({
    mutationFn: createTransactions,
    onSuccess: (data) => {
      // toast({
      //   variant: "default",
      //   title: "Transactions created!",
      //   description: `${data.length} transactions created successfully.`,
      // });
      queryClient.invalidateQueries("transactions");
      queryClient.invalidateQueries("categories");
      hide();
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Failed to create transactions",
        description: "Please try again later.",
      });
    },
  });

  const editTransaction = async (record: Transaction) => {
    const response = await TransactionAPI.patch(record.id, {
      occurred: formatDate(record.occurred),
      category: normalizeRelationId(record.category),
      type: normalizeRelationId(record.type),
      amount: record.amount,
      description: record.description,
      user: user?.id,
    });
    return response.data;
  };

  const editTransactionMutation = useMutation({
    mutationFn: (transaction: Partial<Transaction>) =>
      editTransaction(transaction as Transaction),
    onSuccess: (data, variables, context) => {
      toast({
        variant: "default",
        title: "Transaction updated!",
        description: `Transaction for ${variables.description} updated successfully.`,
      });
      queryClient.invalidateQueries("transactions");
      queryClient.invalidateQueries("categories");
      hide();
    },
    onError: (error, variables, context) => {
      toast({
        variant: "destructive",
        title: "Failed to update transaction",
        description: "Please try again later.",
      });
    },
  });

  const editTransactions = async (
    records: Partial<Transaction>[],
    category: Category,
  ) => {
    const response = await TransactionAPI.bulkPatch(records, category);
    return response.data;
  };

  const editTransactionsMutation = useMutation({
    mutationFn: (data: {
      records: Partial<Transaction>[];
      category: Category;
    }) => editTransactions(data.records, data.category),
    onSuccess: (data, variables, context) => {
      toast({
        variant: "default",
        title: "Transactions updated!",
        description: `${variables.records.length} transactions updated successfully.`,
      });
      queryClient.invalidateQueries("transactions");
      queryClient.invalidateQueries("categories");
      hide();
    },
    onError: (error, variables, context) => {
      toast({
        variant: "destructive",
        title: "Failed to update transactions",
        description: "Please try again later.",
      });
    },
  });

  const deleteTransaction = async (record: Transaction) => {
    const response = await TransactionAPI.delete(`${record.id}`);
    return response.data;
  };

  const deleteTransactionMutation = useMutation({
    mutationFn: (transaction: Transaction) => deleteTransaction(transaction),
    onSuccess: (data, variables, context) => {
      toast({
        variant: "default",
        title: "Transaction deleted!",
        description: `Transaction for ${variables.description} deleted successfully.`,
      });
      queryClient.invalidateQueries("transactions");
      queryClient.invalidateQueries("categories");
      hide();
    },
    onError: (error, variables, context) => {
      toast({
        variant: "destructive",
        title: "Failed to delete transaction",
        description: "Please try again later.",
      });
    },
  });

  const deleteTransactions = async (records: Partial<Transaction>[]) => {
    const response = await TransactionAPI.bulkDelete(records);
    return response.data;
  };

  const deleteTransactionsMutation = useMutation({
    mutationFn: (records: Partial<Transaction>[]) =>
      deleteTransactions(records),
    onSuccess: (data, variables, context) => {
      toast({
        variant: "default",
        title: "Transactions deleted!",
        description: `${variables.length} transactions deleted successfully.`,
      });
      queryClient.invalidateQueries("transactions");
      queryClient.invalidateQueries("categories");
      hide();
    },
    onError: (error, variables, context) => {
      toast({
        variant: "destructive",
        title: "Failed to delete transactions",
        description: "Please try again later.",
      });
    },
  });

  const fetchTransactions = async () => {
    if (!isAuthenticated) return [];
    const params = {
      ...buildTransactionFilterParams({ filters, search, categories, types }),
      limit: pagination?.limit ?? 20,
      offset: pagination?.offset ?? 0,
      ordering: pagination?.ordering ?? "occurred",
    };
    const response = await TransactionAPI.getAll(params);
    setCount(response.data?.count ?? 0);
    return response?.data?.results ?? [];
  };

  const { data: transactions, isFetching: loadingTransactions } = useQuery<
    Transaction[]
  >(
    [
      "transactions",
      categories,
      types,
      filters?.categories,
      filters?.type,
      filters?.startDate,
      filters?.endDate,
      filters?.created,
      filters?.customCategories,
      search,
      pagination.ordering,
      pagination.orderBy,
      pagination.pageSize,
      pagination.page,
      pagination.limit,
      pagination.offset,
    ],
    delayPromise(fetchTransactions, 500),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      // initialData: MOCK_TRANSACTION_DATA,
      enabled: isAuthenticated && fetchEnabled,
    },
  );

  return {
    transactions,
    loadingTransactions,
    predictTransactions,
    predictTransactionsMutation,
    predictTransactionsAbort,
    createTransaction,
    createTransactionMutation,
    createTransactions,
    createTransactionsMutation,
    createTransactionsAbort,
    editTransaction,
    editTransactionMutation,
    editTransactions,
    editTransactionsMutation,
    deleteTransaction,
    deleteTransactionMutation,
    deleteTransactions,
    deleteTransactionsMutation,
  };
};
export default useTransactions;
